import { createFileRoute, getRouteApi, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { createTablePage } from '~/components/ui/createTablePage';
import { BackLink } from '~/components/ui/NavLinks';
import { CreateCourseButton } from './CreateCourseButton';

type CourseAdminSummary = components['schemas']['CourseAdminSummary'];

// Optional key, not an undefined value: a required key would force every
// `<Link to="/courses">` in the console to pass `search`.
type CoursesSearch = { series?: number };

// The route names this component and the component reads the route's search, so
// the two infer circularly. getRouteApi plus an explicit CoursesSearch breaks it.
const routeApi = getRouteApi('/_admin/courses/');

const columnHelper = createColumnHelper<CourseAdminSummary>();

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('version', { header: 'Version' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('language', {
    header: 'Language',
    // Null for migrated courses that predate series assignment.
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.accessor('status', { header: 'Status' }),
];

const CoursesList = createTablePage<
  CourseAdminSummary,
  '/courses/$courseId/$version'
>({
  name: 'Courses',
  // `/courses?series=N` scopes the list, so say so and offer a way back —
  // matching the lessons list, which links back to the course scoping it.
  backLink: () => {
    const { series } = routeApi.useSearch() as CoursesSearch;
    if (series === undefined) return null;
    return (
      <BackLink>
        <Link to="/series/$seriesId" params={{ seriesId: String(series) }}>
          &larr; Back to series
        </Link>
      </BackLink>
    );
  },
  // Admin endpoint: all statuses (the student list_courses is gated to a fully
  // published path), scoped to one series when the route's search says so.
  useData: (page: number) => {
    const { series } = routeApi.useSearch() as CoursesSearch;
    return api.adminListCourses.useQuery({
      query: {
        limit: 10,
        offset: 10 * page,
        ...(series !== undefined ? { series_id: series } : {}),
      },
    });
  },
  columns,
  itemBaseUrl: '/courses/$courseId/$version',
  getParams: (row) => ({
    courseId: String(row.id),
    version: String(row.version),
  }),
  actions: <CreateCourseButton />,
});

export const Route = createFileRoute('/_admin/courses/')({
  validateSearch: (s: Record<string, unknown>): CoursesSearch => {
    const series = Number(s.series);
    return Number.isFinite(series) && series > 0 ? { series } : {};
  },
  component: CoursesList,
});
